/**
 * 🔍 BarcodeScanner v9 — Iframe-based (workaround React lifecycle issues)
 *
 * El componente React montaba/desmontaba html5-qrcode en Safari iOS de forma que
 * el stream se inicializaba pero la decodificación NO arrancaba. La página standalone
 * /barcode-scanner-frame.html SÍ detecta perfectamente. Esta versión usa esa página
 * dentro de un <iframe> y se comunica con ella vía postMessage.
 *
 * Props públicas SIN cambios — drop-in replacement de las versiones anteriores:
 *   isActive: bool          → pause/resume vía postMessage
 *   onScan(text, format)    → llamado cuando el iframe detecta un barcode
 *   onError(err)            → opcional, llamado si la cámara falla
 *   facingMode              → 'environment' | 'user'
 *   onManualEntry(code)     → input manual (renderizado fuera del iframe)
 */
import { useEffect, useRef, useState } from 'react';
import {
  Camera, CameraOff, RefreshCw, AlertTriangle, Keyboard, Search,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const FRAME_SRC = '/barcode-scanner-frame.html';

export default function BarcodeScanner({
  isActive = true,
  onScan,
  onError,
  facingMode = 'environment',
  onManualEntry,
}) {
  const iframeRef = useRef(null);
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const [activeFacing, setActiveFacing] = useState(facingMode);
  const [iframeReady, setIframeReady] = useState(false);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');

  // ── Listen for messages from the iframe ──
  useEffect(() => {
    const handler = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object' || !data.type) return;
      // Only react to our own protocol
      if (!String(data.type).startsWith('VEXLY_SCANNER_')) return;
      // Defensive: ensure source is our iframe
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;

      switch (data.type) {
        case 'VEXLY_SCANNER_READY':
          console.log('[BarcodeScanner/iframe] READY');
          setIframeReady(true);
          setError(null);
          break;
        case 'VEXLY_SCANNER_DETECTED':
          console.log('[BarcodeScanner/iframe] DETECTED', data.value, data.format);
          try { onScanRef.current?.(data.value, data.format || 'unknown'); }
          catch (e) { console.error('[BarcodeScanner/iframe] onScan threw:', e); }
          break;
        case 'VEXLY_SCANNER_ERROR':
          console.warn('[BarcodeScanner/iframe] ERROR', data.error);
          setError(data.error || 'Error desconocido');
          try { onErrorRef.current?.(new Error(data.error || 'Camera error')); } catch {}
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // ── Send pause/resume commands to iframe when isActive changes ──
  useEffect(() => {
    if (!iframeReady) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      win.postMessage({ type: isActive ? 'VEXLY_SCANNER_RESUME' : 'VEXLY_SCANNER_PAUSE' }, '*');
    } catch {}
  }, [isActive, iframeReady]);

  // ── Cleanup on unmount: tell iframe to stop ──
  useEffect(() => {
    const iframe = iframeRef.current;
    return () => {
      try { iframe?.contentWindow?.postMessage({ type: 'VEXLY_SCANNER_STOP' }, '*'); } catch {}
    };
  }, []);

  const flipCamera = () => {
    const next = activeFacing === 'environment' ? 'user' : 'environment';
    setActiveFacing(next);
    setIframeReady(false);
    setError(null);
    // Reload the iframe with new facingMode. Mantiene el mismo elemento, solo cambia src.
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault?.();
    const code = (manualCode || '').trim();
    if (!code) return;
    try {
      if (typeof onManualEntry === 'function') onManualEntry(code);
      else onScanRef.current?.(code, 'manual');
    } catch (err) { console.error(err); }
    setManualCode('');
  };

  const iframeSrc = `${FRAME_SRC}?facingMode=${activeFacing}&t=${activeFacing}`;

  return (
    <div className="w-full" data-testid="barcode-scanner">
      <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden border border-border">
        <iframe
          ref={iframeRef}
          key={activeFacing}
          src={iframeSrc}
          title="Escáner de código de barras"
          allow="camera; microphone"
          className="w-full h-full border-0"
          data-testid="barcode-scanner-iframe"
        />

        <button
          type="button"
          onClick={flipCamera}
          className="absolute top-2 right-2 p-2.5 rounded-full bg-black/70 hover:bg-black/85 active:scale-95 text-white shadow-lg"
          aria-label="Cambiar cámara"
          data-testid="scanner-toggle-cam"
          title="Cambiar cámara (trasera/frontal)"
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        {!iframeReady && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-sm pointer-events-none">
            <Camera className="w-5 h-5 mr-2 animate-pulse" /> Iniciando cámara…
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 text-white p-4 text-center" data-testid="scanner-error">
            <AlertTriangle className="w-8 h-8 mb-2 text-amber-400" />
            <p className="text-sm font-semibold">No se pudo abrir la cámara</p>
            <p className="text-xs opacity-80 mt-1 max-w-xs">{error}</p>
            <p className="text-xs opacity-70 mt-2 max-w-xs">
              Verifica permisos. Usa <strong>entrada manual</strong> aquí abajo.
            </p>
          </div>
        )}

        {!isActive && !error && iframeReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-sm pointer-events-none">
            <CameraOff className="w-5 h-5 mr-2" /> Cámara pausada
          </div>
        )}
      </div>

      {/* Manual entry inline */}
      <form
        onSubmit={handleManualSubmit}
        className="mt-2 flex items-center gap-2"
        data-testid="manual-entry-form"
      >
        <div className="relative flex-1">
          <Keyboard className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Escribe o pega el código de barras…"
            className="pl-8 h-10 text-sm"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            data-testid="manual-entry-input"
          />
        </div>
        <Button
          type="submit"
          disabled={!manualCode.trim()}
          className="h-10 px-3"
          data-testid="manual-entry-submit"
        >
          <Search className="w-4 h-4 mr-1" /> Buscar
        </Button>
      </form>
    </div>
  );
}
